import { ChangeEvent, MouseEvent, useContext, useEffect, useState } from 'react';
import { Box, IconButton, InputAdornment, Link, TextField } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { MetadataError } from '@ep/error-handling';
import { FirebaseError } from 'firebase/app';
import {
  EmailAuthProvider,
  linkWithCredential,
  multiFactor,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  OAuthCredential,
  PhoneMultiFactorGenerator,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  unlink,
  User,
  UserCredential
} from 'firebase/auth';
import { BodyText, CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';
import PrimaryButton from '../../shared/button/PrimaryButton';
import {
  auth,
  clearRecaptchaData,
  finishMfaSignIn,
  generateMfaResolver,
  generateRecaptchaVerifier,
  getRecaptchaVerifier,
  retrieveMultiFactorResolver,
  setIsAuthLinkingInProgress,
  startSmsMfaSignin
} from '../../../lib/google/firebase';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

interface LinkAccountsFederatedProps {
  email: string;
  pending_credential: OAuthCredential | null;
  returnToIdentifier: () => void;
}

type MfaStep = 'none' | 'sms_code' | 'totp_code';

/**
 * Handles the one-time account linking step when a password-provider user signs in with Microsoft SSO.
 * @param {string} email The email address pre-populated from the SSO collision error.
 * @param {OAuthCredential | null} pending_credential The Microsoft credential extracted from the collision error.
 * @param {() => void} returnToIdentifier Navigates back to the login identifier step.
 * @returns {JSX.Element}
 */
const LinkAccountsFederated = ({
  email,
  pending_credential,
  returnToIdentifier
}: LinkAccountsFederatedProps): JSX.Element => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [password, setPassword] = useState<string>('');
  const [passwordFocused, setPasswordFocus] = useState<boolean>(false);
  const [validPassword, setValidPassword] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [linkLoad, setLinkLoad] = useState<boolean>(false);
  const [linkFault, setLinkFault] = useState<boolean>(false);
  const [linkFaultMessage, setLinkFaultMessage] = useState<string>('');
  const [resetEmailSent, setResetEmailSent] = useState<boolean>(false);
  const [linked, setLinked] = useState<boolean>(false);
  const [mfaStep, setMfaStep] = useState<MfaStep>('none');
  const [mfaCode, setMfaCode] = useState<string>('');
  const [mfaFault, setMfaFault] = useState<string>('');
  const [mfaLoad, setMfaLoad] = useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      setValidPassword(password.length > 0);
    }, 500);
    return () => {
      null;
    };
  }, [password, passwordFocused]);

  const handleForgotPassword = async (): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
      setLinkFault(true);
      setLinkFaultMessage(`Password reset link sent to ${email}`);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE('handleForgotPassword'),
          { email },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to send reset email. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const completeLinking = async (user: User): Promise<void> => {
    setIsAuthLinkingInProgress(true);
    try {
      if (!pending_credential) return;

      // Remove all MFA enrollment from users migrating from MFA to federated provider.
      for (const factor of multiFactor(user).enrolledFactors) {
        await multiFactor(user).unenroll(factor);
      }
      await unlink(user, EmailAuthProvider.PROVIDER_ID);
      await linkWithCredential(user, pending_credential);
      await signOut(auth);
      setLinked(true);
    } finally {
      setIsAuthLinkingInProgress(false);
    }
  };

  const handleLinkFederatedProvider = async (
    event: MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    clearRecaptchaData();
    setLinkLoad(true);
    setLinkFault(false);
    try {
      const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);
      await completeLinking(result.user);
    } catch (err: unknown) {
      if ((err as FirebaseError).code === 'auth/multi-factor-auth-required') {
        const resolver: MultiFactorResolver = generateMfaResolver(err as MultiFactorError);
        const hint: MultiFactorInfo = resolver.hints[0];
        if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
          await startSmsMfaSignin(
            hint,
            resolver.session,
            getRecaptchaVerifier() ?? generateRecaptchaVerifier()
          );
          setMfaStep('sms_code');
        } else {
          setMfaStep('totp_code');
        }
      } else if (
        (err as FirebaseError).code === 'auth/wrong-password' ||
        (err as FirebaseError).code === 'auth/invalid-credential'
      ) {
        setLinkFault(true);
        setLinkFaultMessage('Incorrect password, please try again.');
      } else {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : FUNCTION_UNKNOWN_FAILURE('handleLinkFederatedProvider'),
            { email },
            tracking_id
          )
        );
        setFeedbackData({
          message: `Failed to link account. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
    } finally {
      setLinkLoad(false);
    }
  };

  const handleVerifySmsMfa = async (): Promise<void> => {
    setMfaLoad(true);
    setMfaFault('');
    setIsAuthLinkingInProgress(true);
    try {
      await finishMfaSignIn(mfaCode);
      if (auth.currentUser) {
        await completeLinking(auth.currentUser);
      }
    } catch (err: unknown) {
      setIsAuthLinkingInProgress(false);
      if (
        (err as FirebaseError).code === 'auth/invalid-verification-code' ||
        (err as FirebaseError).code === 'auth/code-expired'
      ) {
        setMfaFault('Incorrect or expired code, please try again.');
      } else {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE('handleVerifySmsMfa'),
            { email },
            tracking_id
          )
        );
        setFeedbackData({
          message: `Failed to verify code. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
    } finally {
      setMfaLoad(false);
    }
  };

  const handleVerifyTotpMfa = async (): Promise<void> => {
    setMfaLoad(true);
    setMfaFault('');
    setIsAuthLinkingInProgress(true);
    try {
      const resolver = retrieveMultiFactorResolver();
      const hint = resolver!.hints[0];
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode);
      const userCredential: UserCredential = await resolver!.resolveSignIn(assertion);
      await completeLinking(userCredential.user);
    } catch (err: unknown) {
      setIsAuthLinkingInProgress(false);
      if ((err as FirebaseError).code === 'auth/invalid-verification-code') {
        setMfaFault('Incorrect code, please try again.');
      } else {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE('handleVerifyTotpMfa'),
            { email },
            tracking_id
          )
        );
        setFeedbackData({
          message: `Failed to verify code. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
    } finally {
      setMfaLoad(false);
    }
  };

  if (linked) {
    return (
      <Box>
        <Subheading weight="Medium" color="primary" sx={{ textAlign: 'center', mb: 4 }}>
          Account Linked
        </Subheading>
        <BodyText sx={{ textAlign: 'center', my: 2 }}>
          Your Microsoft account has been linked. You can now sign in using Microsoft.
        </BodyText>
        <PrimaryButton sx={{ mt: 4, width: '100%' }} onClick={returnToIdentifier}>
          CONTINUE TO SIGN IN
        </PrimaryButton>
      </Box>
    );
  }

  if (mfaStep !== 'none') {
    return (
      <Box>
        <Box id="recaptcha-container" />
        <Subheading weight="Medium" color="primary" sx={{ textAlign: 'center', mb: 4 }}>
          Verify Your Identity
        </Subheading>
        <BodyText sx={{ my: 2 }}>
          {mfaStep === 'sms_code'
            ? 'Enter the SMS code sent to your phone to continue.'
            : 'Enter the code from your authenticator app to continue.'}
        </BodyText>
        <TextField
          label="Verification Code"
          fullWidth
          variant="outlined"
          value={mfaCode}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setMfaCode(event.target.value)}
          sx={{ my: 2 }}
        />
        {mfaFault && <CaptionText sx={{ mb: 2, color: 'red' }}>{mfaFault}</CaptionText>}
        <PrimaryButton
          disabled={mfaCode.length === 0}
          loading={mfaLoad}
          sx={{ mt: 4, width: '100%' }}
          onClick={mfaStep === 'sms_code' ? handleVerifySmsMfa : handleVerifyTotpMfa}
        >
          VERIFY
        </PrimaryButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box id="recaptcha-container" />
      <Subheading weight="Medium" color="primary" sx={{ textAlign: 'center', mb: 4 }}>
        Link Your Account
      </Subheading>
      <BodyText sx={{ my: 2 }}>
        Your account already exists with a password, but your organisation is set to log in with
        Microsoft. Enter your existing <strong>Syntiro</strong> password to link your Microsoft
        account for sign in. You won&apos;t need to do this again.
      </BodyText>
      <form>
        <TextField
          fullWidth
          variant="outlined"
          value={email}
          sx={{ my: 1 }}
          slotProps={{ htmlInput: { autoComplete: 'username' } }}
          InputProps={{ readOnly: true }}
        />
        <TextField
          error={!validPassword && passwordFocused}
          onBlur={() => setPasswordFocus(true)}
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          variant="outlined"
          onInput={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
          value={password}
          sx={{ my: 2 }}
          slotProps={{ htmlInput: { autoComplete: 'current-password' } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {linkFault && (
          <CaptionText
            sx={{
              mb: 2,
              color: resetEmailSent ? '#01579B' : 'red'
            }}
          >
            {linkFaultMessage}
          </CaptionText>
        )}
        {!resetEmailSent && (
          <Box sx={{ mt: 1 }}>
            <Link href="#" onClick={handleForgotPassword} sx={{ color: '#053d5d' }}>
              Forgot password?
            </Link>
          </Box>
        )}
        <PrimaryButton
          disabled={!validPassword}
          loading={linkLoad}
          sx={{ mt: 4, width: '100%' }}
          onClick={handleLinkFederatedProvider}
          type="submit"
        >
          LINK ACCOUNT
        </PrimaryButton>
      </form>
    </Box>
  );
};

export default LinkAccountsFederated;
